import React from 'react';

import { Paper, Tooltip } from '@mui/material';

import {
    formatDurationHours,
    formatFunnelPercent,
    formatInteger
} from '../utils/numberUtils';
import { formatCurrency } from '../utils/stringFormat';

const funnelTones = [
    { bg: '#ffd24a', color: '#172528' },
    { bg: '#ffbe32', color: '#172528' },
    { bg: '#ffa51f', color: '#172528' },
    { bg: '#f28a18', color: '#ffffff' },
    { bg: '#e66f22', color: '#ffffff' },
    { bg: '#d9582d', color: '#ffffff' },
    { bg: '#c94437', color: '#ffffff' },
    { bg: '#b9323f', color: '#ffffff' }
];

const EMPTY_VALUE = '—';
const LOW_CONVERSION_THRESHOLD = 50;
const INSIDE_LABEL_MIN_WIDTH = 22;

const buildStageViewModel = (item, index, funnelData) => {
    const previousStage = funnelData[index - 1];
    const nextStage = funnelData[index + 1];
    const maxCount = Math.max(...funnelData.map((stage) => stage.count), 1);
    const widthPercent = Math.max((item.count / maxCount) * 100, 0);
    const conversionFromPrevious = previousStage
        ? (previousStage.count > 0 ? (item.count / previousStage.count) * 100 : 0)
        : null;
    const hasTicket = item.totalTicket !== null && item.totalTicket !== undefined;

    return {
        tone: funnelTones[index] ?? funnelTones.at(-1),
        previousStage,
        nextStage,
        widthPercent,
        labelInside: widthPercent >= INSIDE_LABEL_MIN_WIDTH,
        conversionFromPrevious,
        conversionText: previousStage ? `${formatFunnelPercent(item.count, previousStage.count)}%` : EMPTY_VALUE,
        isLowConversion: conversionFromPrevious !== null && conversionFromPrevious < LOW_CONVERSION_THRESHOLD,
        conversionToNext: formatFunnelPercent(nextStage?.count ?? item.count, item.count),
        conversionFromStart: formatFunnelPercent(item.count, funnelData[0]?.count ?? 0),
        dropOff: previousStage ? Math.max(previousStage.count - item.count, 0) : 0,
        hasTicket,
        averageTicket: hasTicket && item.count > 0 ? item.totalTicket / item.count : 0,
        averageDurationText: formatDurationHours(item.averageHours),
        medianDurationText: formatDurationHours(item.medianHours)
    };
};

const StageTooltipContent = ({ item, stage }) => (
    <div className='crm-dashboard-funnel-tooltip-content'>
        <p className='crm-dashboard-funnel-tooltip-title'>{item.label}</p>
        <p>Volume: <strong>{formatInteger(item.count ?? 0)}</strong></p>
        {stage.hasTicket ? <p>Ticket total: <strong>{formatCurrency(item.totalTicket)}</strong></p> : null}
        {stage.hasTicket ? <p>Ticket m&eacute;dio: <strong>{formatCurrency(stage.averageTicket)}</strong></p> : null}
        <p>{stage.nextStage ? `Conversão para ${stage.nextStage.label}` : 'Taxa de sucesso'}: <strong>{stage.conversionToNext}%</strong></p>
        <p>Convers&atilde;o acumulada: <strong>{stage.conversionFromStart}%</strong></p>
        {stage.previousStage ? <p>Perda desde a etapa anterior: <strong>{formatInteger(stage.dropOff ?? 0)}</strong></p> : null}
        {stage.averageDurationText ? <p>Tempo m&eacute;dio na etapa: <strong>{stage.averageDurationText}</strong></p> : null}
        {stage.medianDurationText ? <p>Tempo mediano na etapa: <strong>{stage.medianDurationText}</strong></p> : null}
    </div>
);

const FunnelStageRow = ({ index, item, funnelData }) => {
    const stage = buildStageViewModel(item, index, funnelData);

    return (
        <Tooltip
            arrow
            placement='top'
            slotProps={{
                tooltip: {
                    className: 'crm-dashboard-funnel-tooltip'
                }
            }}
            title={<StageTooltipContent item={item} stage={stage} />}
        >
            <div
                className='crm-dashboard-funnel-row'
                style={{
                    '--stage-width': `${stage.widthPercent}%`,
                    '--stage-bg': stage.tone.bg,
                    '--stage-color': stage.tone.color
                }}
            >
                <span className='crm-dashboard-funnel-index'>{String(index + 1).padStart(2, '0')}</span>
                <span className='crm-dashboard-funnel-label'>
                    <span className='crm-dashboard-funnel-dot' />
                    {item.label}
                </span>
                <div className='crm-dashboard-funnel-track'>
                    <div className={`crm-dashboard-funnel-bar${stage.labelInside ? ' has-inside-label' : ''}`}>
                        {stage.labelInside ? <span className='crm-dashboard-funnel-bar-value'>{formatInteger(item.count ?? 0)}</span> : null}
                    </div>
                    {!stage.labelInside ? <span className='crm-dashboard-funnel-bar-value is-outside'>{formatInteger(item.count ?? 0)}</span> : null}
                </div>
                <span className={`crm-dashboard-funnel-conversion${stage.isLowConversion ? ' is-low' : ''}${stage.previousStage ? '' : ' is-empty'}`}>
                    {stage.conversionText}
                </span>
                <span className={`crm-dashboard-funnel-duration${stage.averageDurationText ? '' : ' is-empty'}`}>
                    {stage.averageDurationText ?? EMPTY_VALUE}
                </span>
            </div>
        </Tooltip>
    );
};

export const FunnelPanel = ({
    funnelData,
    subtitle = 'Visualização da jornada do cliente de ponta a ponta',
    title = 'Funil de Conversão'
}) => (
    <Paper className='crm-dashboard-panel'>
        <div className='crm-dashboard-panel-header'>
            <h2 className='crm-dashboard-panel-title'>{title}</h2>
            <p className='crm-dashboard-panel-subtitle'>{subtitle}</p>
        </div>

        <div className='crm-dashboard-funnel'>
            <div className='crm-dashboard-funnel-row crm-dashboard-funnel-head' aria-hidden='true'>
                <span className='crm-dashboard-funnel-index'>#</span>
                <span className='crm-dashboard-funnel-label'>Etapa</span>
                <span className='crm-dashboard-funnel-track'>Volume</span>
                <span className='crm-dashboard-funnel-conversion'>Conversão</span>
                <span className='crm-dashboard-funnel-duration'>Tempo méd.</span>
            </div>
            {funnelData.map((item, index) => (
                <FunnelStageRow key={item.label} index={index} item={item} funnelData={funnelData} />
            ))}
        </div>
    </Paper>
);

export default FunnelPanel;
